import * as React from 'react';
import { useState } from 'react';
import classnames from 'classnames';
import {
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Row,
  Col,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
} from 'reactstrap';
import { Can } from '../../auth/Can';
import { AccountAudit } from './AccountAudit';
import { CredentialsTabs } from './CredentialsTabs';
import { ShortLongCodesTab } from './slicktext/ShortLongCodesTab';
import { TextwordsTab } from './slicktext/TextwordsTab';
import { CustomFieldsTab } from './dotdigital/CustomFieldsTab';
import { LyticsTab } from './dotdigital/LyticsTab';
import { AddressbooksTab } from './dotdigital/AddressbooksTab';
import { Feature } from 'flagged';
import { PrivyTab } from './dotdigital/PrivyTab';
import { DDForm } from './dotdigital/DDForm';
import { SlicktextForm } from './slicktext/SlicktextForm';
import { AttentiveForm } from './attentive/AttentiveForm';
import { DataAcquisitionTab } from './DataAcquisitionTab';
import { TreasureDataForm } from './treasure-data/TreasureDataForm';
import { CommunityForm } from './community/CommunityForm';
import { CommunityOptinsTab } from './community/CommunityOptinsTab';
import { DDInsightsTab } from './dotdigital/DDInsightsTab';
import { BrazeForm } from './braze/BrazeForm';
import { BrazeOptinsTab } from './braze/BrazeOptinsTab';
import { BrazeAccountStepper } from './braze/BrazeAccountStepper';
import { DecommissioningStatusTab } from './DecommissioningStatusTab';

const pagesStyles = require('../pages.css');

export const FIELDS_TAB = '1';
export const CREDENTIALS_TAB = '2';
export const ARCHIVED_CREDENTIALS_TAB = '3';
export const SHORT_LONG_CODES_TAB = '4';
export const TEXTWORDS_TAB = '5';
export const CUSTOM_FIELDS_TAB = '6';
export const ADDRESSBOOKS_TAB = '7';
export const LYTICS_TAB = '8';
export const PRIVY_TAB = '9';
export const DATA_ACQUISITION_TAB = '10';
export const COMMUNITY_OPTINS_TAB = '11';
export const DD_INSIGHTS_TAB = '12';
export const BRAZE_OPTINS_TAB = '13';
export const DECOMMISSIONING_TAB = '14';

const systemNames = {
  dotdigital: 'DotDigital',
  slicktext: 'SlickText',
  attentive: 'Attentive',
  treasure_data: 'Treasure Data',
  community: 'Community',
  braze: 'Braze',
} as any;

const accountDecommissionReasons = [
  { key: 'artist_gone', label: 'Artist no longer with UMG' },
  {
    key: 'duplicate_account',
    label: 'Duplicate Account - Inactive',
  },
  { key: 'migrated_to_braze', label: 'Account Migrated to Braze' },
];

const accountDecommissionStatusUserMessageMap = {
  requested: 'Requested',
  pending: 'Pending',
  failed: 'Failed',
  complete: 'Complete',
} as any;

const accountDecommissionReasonUserMessageMap = {
  artist_gone: 'Artist no longer with UMG',
  duplicate_account: 'Duplicate / Inactive Account',
  migrated_to_braze: 'Account Migrated to Braze',
};

export const AccountForm = ({
  save,
  requestAccountDecommission,
  model,
  account,
  system,
  accountOrig,
}: {
  save: (values: any, errors: any) => void;
  requestAccountDecommission?: (key: string) => void;
  model: any;
  account: any;
  system: string;
  accountOrig: any;
}) => {
  // logger.debug(`[AccountForm] model: ${JSON.stringify(model)}`);
  const [activeTab, setActiveTab] = useState('1');
  const [canSave, setCanSave] = useState(true);
  const [accountHasCreds, setAccountHasCreds] = useState(false);
  const [accountHasOptins, setAccountHasOptins] = useState(false);

  const toggle = (tab: any) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  let accountName = '';
  const isDotdigital = system === 'dotdigital';
  const isSlicktext = system === 'slicktext';
  const isAttentive = system === 'attentive';
  const isTreasureData = system === 'treasure_data';
  const isCommunity = system === 'community';
  const isBraze = system === 'braze';
  if (isDotdigital && account && account.ddAccountName)
    accountName = account.ddAccountName;
  if (isSlicktext && account && account.name) accountName = account.name;
  if (isAttentive && account && account.account_name)
    accountName = account.account_name;
  if (isTreasureData && account && account.account_name)
    accountName = account.account_name;
  if (isCommunity && account && account.account_name)
    accountName = account.account_name;
  if (isBraze && account && account.account_name)
    accountName = account.account_name;

  const enableDecomRequest =
    account &&
    (!account?.decommissioning_status ||
      account?.decommissioning_status === 'failed' ||
      false);

  const decommissioningCommenced = !!account?.decommissioning_status?.status; // if there's a value it's been kicked off
  const decommissioningStatus = account?.decommissioning_status?.status;

  return (
    <div className={`${pagesStyles.container} ${pagesStyles.fullHeight}`}>
      <div>
        <h3 style={{ flex: 1 }}>
          {account && `${systemNames[system]}: ${accountName}`}
        </h3>
        <div style={{ marginTop: '16pt', marginBottom: '36pt' }}>
          {account?.decommissioning_status in
            accountDecommissionStatusUserMessageMap && (
            <div style={{ marginBottom: '4pt' }}>
              Account Decommission Status:{' '}
              {
                accountDecommissionStatusUserMessageMap[
                  account.decommissioning_status
                ]
              }
            </div>
          )}
          {enableDecomRequest && (
            <UncontrolledDropdown>
              <DropdownToggle>Request Decommissioning</DropdownToggle>
              <DropdownMenu container="body">
                <DropdownItem disabled style={{}}>
                  <div
                    style={{
                      fontSize: 12,
                    }}
                  >
                    Select Reason for Decommissioning
                  </div>
                </DropdownItem>
                <div
                  style={{
                    backgroundColor: '#e3e3e3',
                    height: 1,
                    margin: 4,
                  }}
                />
                {accountDecommissionReasons.map(({ key, label }) => (
                  <DropdownItem
                    key={key}
                    onClick={() => requestAccountDecommission?.(key)}
                  >
                    <div style={{ fontSize: 12 }}>{label}</div>
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </UncontrolledDropdown>
          )}
        </div>
      </div>
      {isBraze && (
        <BrazeAccountStepper
          account={accountOrig}
          toggle={toggle}
          accountHasCreds={accountHasCreds}
          accountHasOptins={accountHasOptins}
        />
      )}
      <Nav tabs>
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === FIELDS_TAB })}
            onClick={() => {
              toggle(FIELDS_TAB);
            }}
          >
            Account Details
          </NavLink>
        </NavItem>
        {account && isCommunity && (
          <NavItem>
            <NavLink
              className={classnames({
                active: activeTab === COMMUNITY_OPTINS_TAB,
              })}
              onClick={() => {
                toggle(COMMUNITY_OPTINS_TAB);
              }}
            >
              Optins
            </NavLink>
          </NavItem>
        )}
        {account && isBraze && (
          <NavItem>
            <NavLink
              className={classnames({
                active: activeTab === BRAZE_OPTINS_TAB,
              })}
              onClick={() => {
                toggle(BRAZE_OPTINS_TAB);
              }}
            >
              Optins
            </NavLink>
          </NavItem>
        )}
        <Can I="read" a="credential">
          {account && (
            <NavItem>
              <NavLink
                className={classnames({
                  active: activeTab === CREDENTIALS_TAB,
                })}
                onClick={() => {
                  toggle(CREDENTIALS_TAB);
                }}
              >
                Credentials
              </NavLink>
            </NavItem>
          )}
        </Can>
        <Feature name="treasure-data-admin">
          {account && (isDotdigital || isSlicktext || isBraze) && (
            <NavItem>
              <NavLink
                className={classnames({
                  active: activeTab === DATA_ACQUISITION_TAB,
                })}
                onClick={() => {
                  toggle(DATA_ACQUISITION_TAB);
                }}
              >
                Data Acquisition Admin
              </NavLink>
            </NavItem>
          )}
        </Feature>
        <Feature name="dd-insights-admin">
          {account && isDotdigital && (
            <NavItem>
              <NavLink
                className={classnames({
                  active: activeTab === DD_INSIGHTS_TAB,
                })}
                onClick={() => {
                  toggle(DD_INSIGHTS_TAB);
                }}
              >
                DD Insights
              </NavLink>
            </NavItem>
          )}
        </Feature>
        <Feature name="addressbooks-management">
          {account && isDotdigital && (
            <NavItem>
              <NavLink
                className={classnames({
                  active: activeTab === ADDRESSBOOKS_TAB,
                })}
                onClick={() => {
                  toggle(ADDRESSBOOKS_TAB);
                }}
              >
                Addressbooks
              </NavLink>
            </NavItem>
          )}
        </Feature>
        {account && isSlicktext && (
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === TEXTWORDS_TAB })}
              onClick={() => {
                toggle(TEXTWORDS_TAB);
              }}
            >
              Textwords
            </NavLink>
          </NavItem>
        )}
        {account && isSlicktext && (
          <NavItem>
            <NavLink
              className={classnames({
                active: activeTab === SHORT_LONG_CODES_TAB,
              })}
              onClick={() => {
                toggle(SHORT_LONG_CODES_TAB);
              }}
            >
              Short/Long Codes
            </NavLink>
          </NavItem>
        )}
        {account && isDotdigital && (
          <NavItem>
            <NavLink
              className={classnames({
                active: activeTab === CUSTOM_FIELDS_TAB,
              })}
              onClick={() => {
                toggle(CUSTOM_FIELDS_TAB);
              }}
            >
              Custom Fields
            </NavLink>
          </NavItem>
        )}
        <Feature name="lytics">
          {account && isDotdigital && (
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === LYTICS_TAB })}
                onClick={() => {
                  toggle(LYTICS_TAB);
                }}
              >
                Lytics
              </NavLink>
            </NavItem>
          )}
        </Feature>
        <Feature name="privy-fields">
          {account && isDotdigital && (
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === PRIVY_TAB })}
                onClick={() => {
                  toggle(PRIVY_TAB);
                }}
              >
                Privy
              </NavLink>
            </NavItem>
          )}
        </Feature>
        <Can I="read" a="credential">
          {account && (
            <NavItem>
              <NavLink
                className={classnames({
                  active: activeTab === ARCHIVED_CREDENTIALS_TAB,
                })}
                onClick={() => {
                  toggle(ARCHIVED_CREDENTIALS_TAB);
                }}
              >
                Archived Credentials
              </NavLink>
            </NavItem>
          )}
        </Can>
        {account && decommissioningCommenced && (
          <NavItem>
            <NavLink
              className={classnames({
                active: activeTab === DECOMMISSIONING_TAB,
              })}
              onClick={() => {
                toggle(DECOMMISSIONING_TAB);
              }}
            >
              Decommissioning Status
            </NavLink>
          </NavItem>
        )}
      </Nav>
      <TabContent activeTab={activeTab} className={pagesStyles.fullHeight}>
        <TabPane tabId={FIELDS_TAB} className={pagesStyles.fullHeight}>
          <Row>
            <Col sm="12">
              <AccountAudit account={account} system={system} />
            </Col>
          </Row>
          {isDotdigital && (
            <DDForm
              canSave={canSave}
              model={model}
              save={save}
              account={account}
              system={system}
              setCanSave={setCanSave}
              decommissioningCommenced={decommissioningCommenced}
              decommissioningStatus={decommissioningStatus}
            />
          )}
          {isSlicktext && (
            <SlicktextForm
              canSave={canSave}
              model={model}
              save={save}
              account={account}
              system={system}
              setCanSave={setCanSave}
              decommissioningCommenced={decommissioningCommenced}
              decommissioningStatus={decommissioningStatus}
            />
          )}
          {isAttentive && (
            <AttentiveForm
              canSave={canSave}
              model={model}
              save={save}
              account={account}
              system={system}
              setCanSave={setCanSave}
              decommissioningCommenced={decommissioningCommenced}
              decommissioningStatus={decommissioningStatus}
            />
          )}
          {isTreasureData && (
            <TreasureDataForm
              canSave={canSave}
              model={model}
              save={save}
              account={account}
              system={system}
              setCanSave={setCanSave}
              decommissioningCommenced={decommissioningCommenced}
              decommissioningStatus={decommissioningStatus}
            />
          )}
          {isCommunity && (
            <CommunityForm
              canSave={canSave}
              model={model}
              save={save}
              account={account}
              system={system}
              setCanSave={setCanSave}
              decommissioningCommenced={decommissioningCommenced}
              decommissioningStatus={decommissioningStatus}
            />
          )}
          {isBraze && (
            <BrazeForm
              canSave={canSave}
              model={model}
              save={save}
              account={account}
              system={system}
              setCanSave={setCanSave}
              setAccountHasCreds={setAccountHasCreds}
              decommissioningCommenced={decommissioningCommenced}
              decommissioningStatus={decommissioningStatus}
            />
          )}
        </TabPane>
        {account && account.key && (
          <CommunityOptinsTab
            account={account}
            decommissioningCommenced={decommissioningCommenced}
          />
        )}
        {account && account.key && (
          <BrazeOptinsTab
            account={account}
            setAccountHasOptins={setAccountHasOptins}
            decommissioningCommenced={decommissioningCommenced}
          />
        )}
        {account && account.key && (
          <CredentialsTabs
            account={account}
            system={system}
            setAccountHasCreds={setAccountHasCreds}
            decommissioningCommenced={decommissioningCommenced}
          />
        )}
        {decommissioningCommenced && (
          <DecommissioningStatusTab account={account} system={system} />
        )}
        {account && account.key && isSlicktext && (
          <ShortLongCodesTab
            account={account}
            system={system}
            decommissioningCommenced={decommissioningCommenced}
          />
        )}
        {account && isSlicktext && (
          <TextwordsTab
            account={account}
            decommissioningCommenced={decommissioningCommenced}
          />
        )}
        {account && isDotdigital && <CustomFieldsTab accountId={account.id} />}
        <Feature name="lytics">
          {account && isDotdigital && <LyticsTab accountId={account.id} />}
        </Feature>
        <Feature name="addressbooks-management">
          {account && isDotdigital && (
            <AddressbooksTab
              account={account}
              decommissioningCommenced={decommissioningCommenced}
            />
          )}
        </Feature>
        <Feature name="privy-fields">
          {account && isDotdigital && (
            <PrivyTab
              accountId={account.id}
              decommissioningCommenced={decommissioningCommenced}
            />
          )}
        </Feature>
        <Feature name="treasure-data-admin">
          {account && (isDotdigital || isSlicktext || isBraze) && (
            <DataAcquisitionTab
              system={system}
              account={account}
              decommissioningCommenced={decommissioningCommenced}
            />
          )}
        </Feature>
        <Feature name="dd-insights-admin">
          {account && isDotdigital && (
            <DDInsightsTab
              system={system}
              account={account}
              decommissioningCommenced={decommissioningCommenced}
            />
          )}
        </Feature>
      </TabContent>
    </div>
  );
};
